import { useQuery, useSuspenseQuery } from '@tanstack/react-query'

import { API_ENDPOINTS } from '../../../common/apiEndpoints'
import {
  reservationCalendarsResponseSchema,
  type ReservationCalendarsResponse,
} from '../../../common/validations/reservationCalendarSchemas'

import { get } from 'api/apiClient'

export const reservationCalendarsKey = (
  contractUuid: string,
  reservableCategory?: string,
  weekStartDate?: string,
  refreshKey?: string | string[]
) => {
  const baseKey = `${API_ENDPOINTS.CONTRACTS}/${contractUuid}/reservation-calendars`
  const key: Array<string | string[]> = [baseKey]
  if (reservableCategory) {
    key.push(reservableCategory)
  }
  if (weekStartDate) {
    key.push(weekStartDate)
  }
  if (refreshKey) {
    key.push(refreshKey)
  }
  return key
}

const path = (
  contractUuid: string,
  reservableCategory?: string,
  weekStartDate?: string
) => {
  const basePath = API_ENDPOINTS.RESERVATION_CALENDARS.replace(
    ':contractUuid',
    contractUuid
  )
  const searchParams = new URLSearchParams()
  if (reservableCategory) {
    searchParams.set('reservableCategory', reservableCategory)
  }
  if (weekStartDate) {
    searchParams.set('weekStartDate', weekStartDate)
  }
  const search = searchParams.toString()
  return search ? `${basePath}?${search}` : basePath
}

const params = (
  contractUuid: string,
  reservableCategory?: string,
  weekStartDate?: string,
  refreshKey?: string | string[],
  enabled: boolean = true
) => ({
  queryKey: reservationCalendarsKey(
    contractUuid,
    reservableCategory,
    weekStartDate,
    refreshKey
  ),
  queryFn: () =>
    get<ReservationCalendarsResponse>(
      path(contractUuid, reservableCategory, weekStartDate),
      reservationCalendarsResponseSchema
    ),
  staleTime: 0,
  enabled,
})

export const useAPIGetReservationCalendars = (
  contractUuid: string,
  reservableCategory?: string,
  weekStartDate?: string,
  refreshKey?: string | string[],
  enabled: boolean = true
) =>
  useQuery<ReservationCalendarsResponse>(
    params(contractUuid, reservableCategory, weekStartDate, refreshKey, enabled)
  )

export const useAPIGetReservationCalendarsSuspense = (
  contractUuid: string,
  equipmentCategory?: string,
  weekStartDate?: string,
  refreshKey?: string | string[]
) =>
  useSuspenseQuery<ReservationCalendarsResponse>(
    params(contractUuid, equipmentCategory, weekStartDate, refreshKey)
  )
